import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Select, 
  MenuItem, 
  Button, 
  Grid, 
  Typography, 
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Paper,
  Alert,
  Snackbar,
  DialogContentText,
  Chip,
  CircularProgress,
  Stack,
  FormControlLabel,
  Switch,
  TextField
} from '@mui/material';
import axios from 'axios';
import yaml from 'js-yaml';
import url from '../../url.js';
import ForumIcon from '../../assets/返回論壇icon.png';
import RoomSettingsChatroomIcon from '../../assets/RoomSettings/chatroom.png';
import RoomSettingsMindmapIcon from '../../assets/RoomSettings/mindmap.png';
import RoomSettingsTagForumIcon from '../../assets/RoomSettings/tagforum.png';
import RoomSettingsWorkspaceIcon from '../../assets/RoomSettings/workspace.png';
import { getInitialConfig, normalizeSettingsForInitialNodes } from '../../utils/activityInfoAccessors';
import { activityService } from '../../services/activityService';
import ActivityInfo from '../../models/ActivityInfo';
import { useInitialNodeGroups } from '../../hooks/useInitialNodeGroups';
import InitialNodeList from './InitialNodeList';
import {
  buildExportData,
  exportToYAML,
  downloadYAMLFile,
  parseImportData,
  validateImportData,
  analyzePartners,
  buildImportConfirmMessage,
  buildImportSuccessMessage,
  cleanSettings,
  validateAndCleanGroups
} from '../../utils/activitySettingsImportExport';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const ActivitySettingsDialog = ({
  open,
  onClose,
  onActivityDeleted,
  activityId  // ✅ 只需要 activityId
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [initialRoomSetting, setInitialRoomSetting] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const isInitializedRef = useRef(false);
  const fileInputRef = useRef(null);
  const [activityPartners, setActivityPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState('');
  const [removingPartnerId, setRemovingPartnerId] = useState(null);
  const [activityVisibility, setActivityVisibility] = useState(true);
  const [initialActivityVisibility, setInitialActivityVisibility] = useState(true);
  const [activityTitle, setActivityTitle] = useState('');
  const [activityDescription, setActivityDescription] = useState('');
  const [initialActivityTitle, setInitialActivityTitle] = useState('');
  const [initialActivityDescription, setInitialActivityDescription] = useState('');
  
  // 從 API 獲取的活動資訊
  const [activityInfo, setActivityInfo] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // 內部管理 roomSetting
  const [roomSetting, setRoomSetting] = useState(null);
  
  // 使用自定義 Hook 管理引導單元群組
  const {
    groups: nodeGroups,
    addGroup,
    updateGroup,
    removeGroup,
    addNodeToGroup,
    updateNodeInGroup,
    removeNodeFromGroup,
    moveNodeBetweenGroups,
    reorderGroups,
    reorderNodesInGroup,
    resetGroups
  } = useInitialNodeGroups([], (newGroups) => {
    // 當群組變更時，同步更新 roomSetting
    setRoomSetting(prev => ({
      ...prev,
      initialNodeGroups: newGroups
    }));
  });
  
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false
  });
  const [closeConfirmDialog, setCloseConfirmDialog] = useState({
    open: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // 統一的資料載入
  useEffect(() => {
    const loadAllData = async () => {
      if (!open || !activityId) {
        setActivityInfo(null);
        setRoomSetting(null);
        resetGroups([]);
        setInitialRoomSetting(null);
        setActivityPartners([]);
        setPartnersError('');
        setActivityVisibility(true);
        setInitialActivityVisibility(true);
        isInitializedRef.current = false;
        return;
      }

      setActivityLoading(true);
      
      try {
        // 1. 獲取活動基本資訊
        const activityResult = await activityService.getActivityById(activityId);
        if (activityResult.success) {
          setActivityInfo(activityResult.data);
          const nextVisibility = activityResult.data?.isPublic !== false;
          const nextTitle = activityResult.data?.title || '';
          const nextDescription = activityResult.data?.content || '';
          setActivityTitle(nextTitle);
          setActivityDescription(nextDescription);
          setInitialActivityTitle(nextTitle);
          setInitialActivityDescription(nextDescription);
          setActivityVisibility(nextVisibility);
          setInitialActivityVisibility(nextVisibility);
        } else {
          console.error('獲取活動資訊失敗:', activityResult.error);
          setSnackbar({
            open: true,
            message: activityResult.error || '無法獲取活動資訊',
            severity: 'error'
          });
        }

        // 2. 獲取活動設定並同步初始化 nodeGroups
        try {
          const activityInfoData = await ActivityInfo.get(activityId);
          const settings = activityInfoData.settings || {};
          
          // 嘗試讀取新格式的分組數據
          let groups = settings.initialNodeGroups;
          
          // 如果沒有分組數據，嘗試從舊格式轉換
          if (!groups || !Array.isArray(groups) || groups.length === 0) {
            const { nodes: seedNodes } = getInitialConfig({ data: { settings } });
            const validNodes = Array.isArray(seedNodes) ? seedNodes : [];
            
            // 將舊格式轉換為分組格式
            groups = [
              {
                id: 'group-default',
                name: '活動開始',
                timing: 'immediate',
                description: '這些節點會在活動開始時自動出現',
                nodes: validNodes.map((node, index) => ({
                  id: `node-${Date.now()}-${index}`,
                  ...node
                })),
                isDefault: true,
              },
            ];
          } else {
            // 確保所有節點都有 id
            groups = groups.map(group => ({
              ...group,
              nodes: group.nodes.map((node, index) => ({
                id: node.id || `node-${Date.now()}-${index}`,
                ...node
              }))
            }));
          }
          
          const normalizedSettings = {
            ...settings,
            initialNodeGroups: groups
          };
          
          setRoomSetting(normalizedSettings);
          resetGroups(groups);
          setInitialRoomSetting(JSON.parse(JSON.stringify(normalizedSettings)));
          isInitializedRef.current = true;
        } catch (error) {
          console.error('載入設定失敗:', error);
          // 創建預設群組
          const defaultGroups = [
            {
              id: 'group-default',
              name: '活動開始',
              timing: 'immediate',
              description: '這些節點會在活動開始時自動出現',
              nodes: [],
              isDefault: true,
            },
          ];
          setRoomSetting({ initialNodeGroups: defaultGroups });
          resetGroups(defaultGroups);
          setInitialRoomSetting({ initialNodeGroups: defaultGroups });
          isInitializedRef.current = true;
        }

        // 3. 獲取活動夥伴列表
        const partnersResult = await activityService.getActivityPartners(activityId);
        if (partnersResult.success) {
          setActivityPartners(partnersResult.data?.partners || []);
          setPartnersError('');
        } else {
          setActivityPartners([]);
          setPartnersError(partnersResult.error || '無法取得夥伴列表');
        }
      } catch (error) {
        console.error('載入資料失敗:', error);
        setSnackbar({
          open: true,
          message: '載入資料失敗',
          severity: 'error'
        });
      } finally {
        setActivityLoading(false);
      }
    };

    loadAllData();
  }, [open, activityId]);

  // 工具函數
  const getIconByLabel = (label) => {
    switch (label) {
      case 'mindmap':
        return RoomSettingsMindmapIcon;

      case 'chatroom':
        return RoomSettingsChatroomIcon;

      case 'sticker':
        return RoomSettingsMindmapIcon;

      case 'tagged-forum':
        return RoomSettingsTagForumIcon;

      case 'workspace':
        return RoomSettingsWorkspaceIcon;

      default:
        return null;
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // ✅ 重新載入夥伴列表
  const loadActivityPartners = async () => {
    if (!activityId) return;
    
    setPartnersLoading(true);
    try {
      const partnersResult = await activityService.getActivityPartners(activityId);
      if (partnersResult.success) {
        setActivityPartners(partnersResult.data?.partners || []);
        setPartnersError('');
      } else {
        setActivityPartners([]);
        setPartnersError(partnersResult.error || '無法取得夥伴列表');
      }
    } catch (error) {
      console.error('載入夥伴列表失敗:', error);
      setPartnersError('載入夥伴列表失敗');
    } finally {
      setPartnersLoading(false);
    }
  };

  const handleRemovePartner = async (partnerId, partnerName) => {
    if (!activityId || !partnerId) return;
    const confirmed = window.confirm(`確定要從活動中移除「${partnerName || partnerId}」嗎？`);
    if (!confirmed) return;

    try {
      setRemovingPartnerId(partnerId);
      const result = await activityService.removePartnerFromActivity(activityId, partnerId);
      if (result.success) {
        setSnackbar({
          open: true,
          message: `${partnerName || '夥伴'} 已取消`,
          severity: 'success'
        });
        
        // 重新載入夥伴列表
        const partnersResult = await activityService.getActivityPartners(activityId);
        if (partnersResult.success) {
          setActivityPartners(partnersResult.data?.partners || []);
        }
      } else {
        setPartnersError(result.error || '取消夥伴失敗');
        setSnackbar({
          open: true,
          message: result.error || '取消夥伴失敗',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Failed to remove partner:', error);
      setPartnersError('取消夥伴失敗');
      setSnackbar({
        open: true,
        message: '取消夥伴失敗',
        severity: 'error'
      });
    } finally {
      setRemovingPartnerId(null);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 導出活動設定
  const handleExportSettings = async () => {
    if (!activityId || !activityInfo) {
      setSnackbar({
        open: true,
        message: '無法導出設定：活動資訊不完整',
        severity: 'error'
      });
      return;
    }

    try {
      const currentUserId = localStorage.getItem('userId');
      
      // 構建導出數據
      const exportData = await buildExportData({
        activityInfo,
        roomSetting,
        nodeGroups,
        activityPartners,
        currentUserId
      });

      // 轉換為 YAML 格式
      const yamlContent = exportToYAML(exportData);

      // 下載文件
      const fileName = `activity-settings-${activityInfo.title || activityId}-${Date.now()}.yaml`;
      downloadYAMLFile(yamlContent, fileName);

      setSnackbar({
        open: true,
        message: '活動設定已導出為 YAML 格式',
        severity: 'success'
      });
    } catch (error) {
      console.error('導出設定失敗:', error);
      setSnackbar({
        open: true,
        message: '導出設定失敗',
        severity: 'error'
      });
    }
  };

  // 導入活動設定
  const handleImportSettings = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        
        console.log('開始解析文件:', file.name);
        
        // 解析文件
        const importData = parseImportData(content, file.name);
        console.log('解析結果:', importData);
        
        // 驗證數據格式
        validateImportData(importData);
        console.log('驗證通過，準備分析夥伴資料');

        // 分析夥伴資料
        const partnerAnalysis = analyzePartners(importData.partners);
        console.log('夥伴分析結果:', partnerAnalysis);

        // 確認導入
        const confirmMessage = buildImportConfirmMessage(importData, partnerAnalysis);
        const confirmed = window.confirm(confirmMessage);
        if (!confirmed) {
          console.log('用戶取消導入');
          return;
        }

        console.log('開始應用設定');

        // 應用設定
      let settingsToSave = null;
      if (importData.settings) {
        // 清理並應用設定
        const newSettings = cleanSettings(importData.settings);
        console.log('應用新設定:', newSettings);
        setRoomSetting(newSettings);
        
        // 更新節點群組
        if (importData.settings.initialNodeGroups && Array.isArray(importData.settings.initialNodeGroups)) {
          console.log('更新節點群組:', importData.settings.initialNodeGroups.length, '個群組');
          
          const validatedGroups = validateAndCleanGroups(importData.settings.initialNodeGroups);
          console.log('驗證後的群組:', validatedGroups);
          resetGroups(validatedGroups);
          
          // 準備要保存的完整設定
          settingsToSave = {
            ...newSettings,
            initialNodeGroups: validatedGroups
          };
        } else {
          console.log('沒有有效的節點群組資料');
          settingsToSave = newSettings;
        }
      }

      // 自動保存導入的設定
      if (settingsToSave) {
        console.log('自動保存導入的設定...');
        try {
          await ActivityInfo.update({
            activityId,
            userId: localStorage.getItem('userId'),
            settings: settingsToSave,
          });
          
          // 更新快照
          setInitialRoomSetting(JSON.parse(JSON.stringify(settingsToSave)));
          console.log('設定已自動保存');
        } catch (error) {
          // 如果不存在，先創建再更新
          if (error.response?.status === 404 ||
              error.response?.data?.error === 'NOT_FOUND' ||
              error.response?.data?.message?.includes('was not found')) {
            console.log('ActivityInfo 不存在，先創建...');
            await ActivityInfo.create({
              activityId,
              userId: localStorage.getItem('userId'),
            });
            await ActivityInfo.update({
              activityId,
              userId: localStorage.getItem('userId'),
              settings: settingsToSave,
            });
            setInitialRoomSetting(JSON.parse(JSON.stringify(settingsToSave)));
            console.log('創建後設定已保存');
          } else {
            throw error;
          }
        }
      }

      // 顯示成功消息
      const successMessage = buildImportSuccessMessage(partnerAnalysis);
      setSnackbar({
        open: true,
        message: successMessage + '，設定已自動保存。',
        severity: partnerAnalysis.ownedPartnersCount > 0 ? 'success' : 'warning'
      });

      // 保存導入的夥伴配置（用於後續保存時創建夥伴）
      if (partnerAnalysis.ownedPartnersCount > 0) {
        console.log('導入的夥伴配置:', importData.partners.filter(p => !p.isExternal));
      }

      console.log('導入完成');

      } catch (error) {
        console.error('導入設定失敗:', error);
        setSnackbar({
          open: true,
          message: `導入設定失敗：${error.message}`,
          severity: 'error'
        });
      }
    };

    reader.onerror = (error) => {
      console.error('文件讀取失敗:', error);
      setSnackbar({
        open: true,
        message: '文件讀取失敗',
        severity: 'error'
      });
    };

    reader.readAsText(file);
    // 清空 input 值，允許重複導入同一文件
    event.target.value = '';
  };

  // 檢查是否有未保存的更改
  const hasUnsavedChanges = () => {
    if (!initialRoomSetting || !roomSetting) return false;
    
    // 比較群組數據
    const initialGroups = initialRoomSetting.initialNodeGroups || [];
    const currentGroups = nodeGroups || [];
    const groupsChanged = JSON.stringify(initialGroups) !== JSON.stringify(currentGroups);
    
    // 比較 roomSetting（排除 initialNodeGroups，因為已經在上面比較了）
    const currentSettingForCompare = { ...roomSetting };
    delete currentSettingForCompare.initialNodeGroups;
    const initialSettingForCompare = { ...initialRoomSetting };
    delete initialSettingForCompare.initialNodeGroups;
    const settingChanged = JSON.stringify(currentSettingForCompare) !== JSON.stringify(initialSettingForCompare);
    
    const visibilityChanged = activityVisibility !== initialActivityVisibility;
    const titleChanged = activityTitle !== initialActivityTitle;
    const descriptionChanged = activityDescription !== initialActivityDescription;

    return groupsChanged || settingChanged || visibilityChanged || titleChanged || descriptionChanged;
  };

  // 儲存設定（不關閉對話框）
  const handleSave = async () => {
    if (!activityId) {
      setSnackbar({
        open: true,
        message: '無法獲取活動ID',
        severity: 'error'
      });
      return;
    }

    try {
      setIsSaving(true);
      // 保存分組數據
      const settingsToSave = {
        ...roomSetting,
        initialNodeGroups: nodeGroups
      };

      const activityUpdateResult = await activityService.updateActivity(activityId, {
        title: activityTitle,
        content: activityDescription,
        isPublic: activityVisibility
      });

      if (!activityUpdateResult.success) {
        throw new Error(activityUpdateResult.error || '更新活動隱私設定失敗');
      }
      
      // 直接調用 API 保存
      try {
        await ActivityInfo.update({
          activityId,
          userId: localStorage.getItem('userId'),
          settings: settingsToSave,
        });
      } catch (error) {
        // 如果不存在，先創建再更新
        if (error.response?.status === 404 ||
            error.response?.data?.error === 'NOT_FOUND' ||
            error.response?.data?.message?.includes('was not found')) {
          await ActivityInfo.create({
            activityId,
            userId: localStorage.getItem('userId'),
          });
          await ActivityInfo.update({
            activityId,
            userId: localStorage.getItem('userId'),
            settings: settingsToSave,
          });
        } else {
          throw error;
        }
      }
      
      // 保存成功後更新快照
      setInitialRoomSetting(JSON.parse(JSON.stringify(settingsToSave)));
      setInitialActivityVisibility(activityVisibility);
      setInitialActivityTitle(activityTitle);
      setInitialActivityDescription(activityDescription);
      
      setSnackbar({
        open: true,
        message: '設定已儲存',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: '儲存設定失敗，請重試',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!activityId) {
      setSnackbar({
        open: true,
        message: '無法獲取活動ID',
        severity: 'error'
      });
      return;
    }

    try {
      const jwtToken = localStorage.getItem('jwtToken');
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
      
      await axios.delete(`${url.backendHost}api/activities/${activityId}`, {
        headers,
        withCredentials: true
      });

      setSnackbar({
        open: true,
        message: '活動已刪除',
        severity: 'success'
      });
      
      setDeleteConfirmDialog({ open: false });
      onClose();
      
      // 通知父組件活動已刪除，需要刷新列表
      if (onActivityDeleted) {
        onActivityDeleted();
      }
      
      // 延遲一下再關閉，讓用戶看到成功消息
      setTimeout(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
      }, 1500);
    } catch (error) {
      console.error('Error deleting activity:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || '刪除活動失敗',
        severity: 'error'
      });
      setDeleteConfirmDialog({ open: false });
    }
  };

  const nodePlan = {
    title: "新增活動資訊",
    label: "活動資訊",
    tags: "initial",
    helperMsg: "請為你提供的回覆下一個標題，讓其他同學能更快速的了解你提供了什麼資訊！",
    saffoldBody: [
      "活動主題：",
      "活動說明：",
      "活動時間：",
      "可參考內容：",
    ],
    scaffoldWords: ["活動主題：", "活動說明：", "活動時間：", "可參考內容："],
    newEdge: true,
    note: null,
    intro: "請在這邊填寫節點內容",
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => {
          if (hasUnsavedChanges()) {
            setCloseConfirmDialog({ open: true });
          } else {
            onClose();
          }
        }} 
        fullWidth 
        maxWidth="xl"
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              活動室設定
              {activityLoading && <CircularProgress size={20} />}
            </Box>
            {activityInfo?.title && (
              <Box sx={{ 
                fontSize: '0.9rem', 
                fontWeight: 'normal', 
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Chip 
                  label={activityInfo.title} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 'medium' }}
                />
                <Typography variant="caption" color="text.secondary">
                  正在編輯此活動的設定
                </Typography>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="activity settings tabs">
              <Tab label="基本設定" {...a11yProps(0)} />
              <Tab label="活動室模式" {...a11yProps(1)} />
              <Tab label="AI 夥伴" {...a11yProps(2)} />
              <Tab label="設定預覽" {...a11yProps(3)} />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
              <Stack spacing={2}>
                <TextField
                  label="活動名稱"
                  value={activityTitle}
                  onChange={(e) => setActivityTitle(e.target.value)}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="活動說明"
                  value={activityDescription}
                  onChange={(e) => setActivityDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Stack>
            </Paper>
            <Paper sx={{ p: 2, mb: 2 }} elevation={0} variant="outlined">
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    活動可見性
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    公開活動會出現在探索頁，私人活動只會保留給擁有者與已加入者。
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={activityVisibility}
                      onChange={(e) => setActivityVisibility(e.target.checked)}
                    />
                  }
                  label={activityVisibility ? '公開' : '私人'}
                />
              </Stack>
            </Paper>
            <InitialNodeList
              groups={nodeGroups}
              onAddGroup={addGroup}
              onUpdateGroup={updateGroup}
              onRemoveGroup={removeGroup}
              onAddNode={addNodeToGroup}
              onUpdateNode={updateNodeInGroup}
              onRemoveNode={removeNodeFromGroup}
              onReorderGroups={reorderGroups}
              onReorderNodes={reorderNodesInGroup}
              onMoveNodeBetweenGroups={moveNodeBetweenGroups}
              nodePlan={nodePlan}
              activityContent={activityInfo?.content}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  切換活動室模式
                </Typography>
                <Select
                  id="chatmode"
                  value={roomSetting?.chatmode || 'sticker'}
                  onChange={(e) => setRoomSetting({ ...roomSetting, chatmode: e.target.value })}
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value="sticker">Sticker</MenuItem>
                  <MenuItem value="chatroom">Chatroom</MenuItem>
                  <MenuItem value="tagged-forum">Tagged Forum</MenuItem>
                  <MenuItem value="workspace">Workspace</MenuItem>
                  {/* <MenuItem value="ai-research">Research With AI Team</MenuItem> */}
                </Select>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  模式預覽: <p/>選擇不同的活動室模式來改變討論的互動方式
                </Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    style={{ width: '100%', maxWidth: '200px' }}
                    alt="活動室圖示"
                    src={getIconByLabel(roomSetting?.chatmode || 'sticker')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {roomSetting?.chatmode === 'sticker' 
                      ? '使用便利貼模式進行快速且生動的討論'
                      : roomSetting?.chatmode === 'chatroom'
                      ? '連續聊天室對話模式'
                      : roomSetting?.chatmode === 'tagged-forum'
                      ? '使用標籤分類的討論空間，按主題組織內容'
                      : roomSetting?.chatmode === 'workspace'
                      ? '工作區模式：結合節點選擇與工具面板的專業協作空間'
                      : '選擇討論模式'
                    }
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">活動中的 AI 夥伴</Typography>
              <Button
                variant="outlined"
                onClick={() => loadActivityPartners()}
                disabled={partnersLoading}
              >
                重新整理
              </Button>
            </Box>
            {partnersError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {partnersError}
              </Alert>
            )}
            {partnersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activityPartners.length === 0 ? (
                  <Alert severity="info">
                    {activityId
                      ? '目前活動中沒有啟用的 AI 夥伴，請從夥伴市場加入。'
                      : '無法辨識活動 ID，請重新開啟設定視窗。'}
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {activityPartners.map((partner) => (
                      <Paper
                        key={partner.partnerId}
                        sx={{
                          p: 2,
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          justifyContent: 'space-between',
                          gap: 2
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {partner.partnerName}
                          </Typography>
                          {partner.partnerConfig?.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {partner.partnerConfig.description}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={partner.groupId ? `限定小組：${partner.groupId}` : '全域夥伴'}
                              size="small"
                              color={partner.groupId ? 'default' : 'primary'}
                              variant={partner.groupId ? 'outlined' : 'filled'}
                            />
                            {partner.partnerConfig?.category && (
                              <Chip
                                label={`分類：${partner.partnerConfig.category}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip
                              label={`加入時間：${new Date(partner.addedAt || Date.now()).toLocaleString('zh-TW')}`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </Box>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemovePartner(partner.partnerId, partner.partnerName)}
                          disabled={removingPartnerId === partner.partnerId}
                        >
                          {removingPartnerId === partner.partnerId ? '取消中...' : '取消夥伴'}
                        </Button>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                當前設定內容預覽（YAML 格式）
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={handleExportSettings}
                  variant="contained"
                  color="primary"
                  disabled={!activityInfo}
                >
                  導出設定
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outlined"
                  color="primary"
                >
                  導入設定
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".yaml,.yml,.json"
                  style={{ display: 'none' }}
                  onChange={handleImportSettings}
                />
              </Box>
            </Box>
            
            {nodeGroups.length > 0 && activityInfo?.content && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  引導單元預覽（按群組）
                </Typography>
                {nodeGroups.map((group, groupIndex) => (
                  <Box key={group.id} sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {group.name} ({group.timing === 'immediate' ? '立即顯示' : '手動添加'})
                    </Typography>
                    {group.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {group.description}
                      </Typography>
                    )}
                    {group.nodes.map((node, index) => {
                      const integratedContent = activityInfo.content + (node.content ? '\n\n---\n\n' + node.content : '');
                      return (
                        <Card key={node.id} sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                              {node.title || `節點 ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                              {integratedContent}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                    {group.nodes.length === 0 && (
                      <Alert severity="info">此群組中沒有節點</Alert>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            
            <Typography variant="h6" sx={{ marginBottom: 2, marginTop: 3 }}>
              完整設定 YAML
            </Typography>
            <Paper 
              sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5',
                maxHeight: '500px',
                overflow: 'auto'
              }}
            >
              <pre style={{ 
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                {(() => {
                  try {
                    const exportData = {
                      version: '1.0',
                      exportDate: new Date().toISOString(),
                      activityInfo: {
                        title: activityInfo?.title || '',
                        content: activityInfo?.content || '',
                      },
                      settings: {
                        chatmode: roomSetting?.chatmode,
                        initialNodeGroups: nodeGroups,
                        ...roomSetting
                      },
                      partners: activityPartners.map(p => ({
                        name: p.partnerName,
                        partnerId: p.partnerId,
                        groupId: p.groupId
                      }))
                    };
                    return yaml.dump(exportData, {
                      indent: 2,
                      lineWidth: -1,
                      noRefs: true,
                      sortKeys: false
                    });
                  } catch (error) {
                    return '無法生成 YAML 預覽';
                  }
                })()}
              </pre>
            </Paper>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: true })} 
            variant="outlined" 
            color="error"
            sx={{ mr: 'auto' }}
          >
            刪除活動
          </Button>
          <Button 
            onClick={() => {
              if (hasUnsavedChanges()) {
                setCloseConfirmDialog({ open: true });
              } else {
                onClose();
              }
            }} 
            variant="outlined" 
            color="secondary"
          >
            取消
          </Button>
          {hasUnsavedChanges() ? (
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? '儲存中...' : '儲存'}
            </Button>
          ) : (
            <Button
              onClick={onClose}
              variant="contained"
              color="primary"
            >
              確認
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false })}
      >
        <DialogTitle>確認刪除活動</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要刪除這個活動嗎？此操作無法復原。活動將被標記為已刪除，並從列表中隱藏。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmDialog({ open: false })}
            color="primary"
          >
            取消
          </Button>
          <Button
            onClick={handleDeleteActivity}
            color="error"
            variant="contained"
          >
            確認刪除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={closeConfirmDialog.open}
        onClose={() => setCloseConfirmDialog({ open: false })}
      >
        <DialogTitle>有未儲存的變更</DialogTitle>
        <DialogContent>
          <DialogContentText>
            您有未儲存的變更。確定要關閉而不儲存嗎？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCloseConfirmDialog({ open: false })}
            color="primary"
          >
            取消
          </Button>
          <Button
            onClick={() => {
              setCloseConfirmDialog({ open: false });
              onClose();
            }}
            color="error"
            variant="contained"
          >
            不儲存並關閉
          </Button>
          <Button
            onClick={async () => {
              try {
                setIsSaving(true);
                // 直接調用內部的 handleSave 邏輯
                await handleSave();
                
                setCloseConfirmDialog({ open: false });
                onClose();
              } catch (error) {
                console.error('Error saving settings:', error);
                setSnackbar({
                  open: true,
                  message: '儲存設定失敗，請重試',
                  severity: 'error'
                });
              } finally {
                setIsSaving(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? '儲存中...' : '儲存並關閉'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ActivitySettingsDialog;

